import React, { useState, useMemo } from 'react';
import { api } from '../api';
import { css, Label, Modal } from './UI';
import { useDireccionesEntrega, SelectorEntrega, entregaDeOrden, entregaParaApi, entregaValida } from './DireccionEntrega';

// Edición de una orden de compra ya emitida (decisión del usuario, 6 oct 2026).
// Se corrige la misma orden mientras no tenga solicitud de pago; el proveedor no
// cambia (para eso se vuelve a cotizar) y, con la recepción validada, solo la
// entrega y las observaciones. Si Finanzas ya validó y el total sube, el backend
// la regresa a su validación. Las reglas viven en PUT /requisiciones/:id/ordenes-compra/:ocId.

const fmt = (n) => `$${(Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const tasa = (t) => { const n = Number(t); return Number.isFinite(n) && n >= 0 ? n : 16; };

function totales(partidas) {
  let subtotal = 0, iva = 0;
  for (const p of partidas) {
    const imp = r2((Number(p.cantidad) || 0) * (Number(p.precioUnitario) || 0));
    subtotal += imp; iva += r2(imp * tasa(p.tasaIva) / 100);
  }
  return { subtotal: r2(subtotal), iva: r2(iva), total: r2(subtotal + iva) };
}

export default function EditarOrdenCompra({ req, orden, onClose, onGuardada, avisar }) {
  const recibida = Boolean(orden.recepcion?.validada);
  const [form, setForm] = useState(() => ({
    condicionPago: orden.condicionPago,
    observaciones: orden.observaciones || '',
    partidas: (orden.partidas || []).map(p => ({
      codigo: p.codigo || '', descripcion: p.descripcion || '', unidad: p.unidad || '',
      cantidad: String(p.cantidad ?? ''), precioUnitario: String(p.precioUnitario ?? ''), tasaIva: String(p.tasaIva ?? 16),
    })),
    motivo: '',
  }));
  const [enviando, setEnviando] = useState(false);
  const direcciones = useDireccionesEntrega();
  const [entrega, setEntrega] = useState(null); // se fija al cargar el catálogo
  const valorEntrega = entrega || (direcciones ? entregaDeOrden(orden, direcciones) : { id: '', texto: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setP = (i, k, v) => setForm(f => ({ ...f, partidas: f.partidas.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));
  const nuevo = useMemo(() => totales(form.partidas), [form.partidas]);
  const v = req.validacionFinanzas || {};
  const regresaAFinanzas = v.aplica && v.estado === 'aprobada' && nuevo.total > (orden.total || 0) + 0.005;

  const guardar = async () => {
    if (!form.motivo.trim()) { avisar('Escribe el motivo del cambio', 'error'); return; }
    // Solo si se tocó: las órdenes anteriores al catálogo pueden no traer entrega
    if (entrega && !entregaValida(entrega)) { avisar('Elige la dirección de entrega', 'error'); return; }
    setEnviando(true);
    try {
      // La entrega solo viaja si se cambió: así una orden sin catálogo no se reescribe sola
      const body = { motivo: form.motivo, observaciones: form.observaciones, ...(entrega ? entregaParaApi(entrega) : {}) };
      if (!recibida) { body.condicionPago = form.condicionPago; body.partidas = form.partidas; }
      const doc = await api.put(`/requisiciones/${req._id}/ordenes-compra/${orden._id}`, body);
      avisar(regresaAFinanzas ? `${orden.folio} actualizada. El total subió: regresa a validación de Finanzas.` : `${orden.folio} actualizada.`);
      onGuardada(doc);
    } catch (e) { avisar(e.message, 'error'); setEnviando(false); }
  };

  const celda = { padding: '4px 4px', verticalAlign: 'top' };
  const inp = (i, k, extra = {}) => (
    <input value={form.partidas[i][k]} onChange={e => setP(i, k, e.target.value)} disabled={recibida} aria-label={k}
      style={{ ...css.input(false), padding: '6px 8px', fontSize: 12.5, ...extra }} {...(['cantidad', 'precioUnitario', 'tasaIva'].includes(k) ? { inputMode: 'decimal' } : {})} />
  );

  return (
    <Modal open onClose={() => !enviando && onClose()} title={`Editar orden ${orden.folio}`} width={860} zIndex={1100}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 12.5, color: 'var(--glass)' }}>
          Proveedor: <b style={{ color: 'var(--ink)' }}>{orden.proveedor}</b> (no se cambia aquí; para cambiarlo, vuelve a cotizar).
          {' '}Revisión actual: {orden.revision || 1}.
          {recibida && <div style={{ color: '#b45309', marginTop: 4 }}>La recepción ya se validó: solo se pueden cambiar la entrega y las observaciones.</div>}
        </div>

        <div>
          <Label>Partidas</Label>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 680 }}>
              <thead><tr>{['Código', 'Descripción', 'Unidad', 'Cantidad', 'Precio unit.', 'IVA %', 'Importe', ''].map((h, i) => <th key={i} scope="col" style={{ textAlign: i >= 3 && i <= 6 ? 'right' : 'left', padding: '4px 4px', fontSize: 11 }}>{h}</th>)}</tr></thead>
              <tbody>
                {form.partidas.map((p, i) => (
                  <tr key={i}>
                    <td style={{ ...celda, width: 90 }}>{inp(i, 'codigo')}</td>
                    <td style={celda}>{inp(i, 'descripcion')}</td>
                    <td style={{ ...celda, width: 70 }}>{inp(i, 'unidad')}</td>
                    <td style={{ ...celda, width: 80 }}>{inp(i, 'cantidad', { textAlign: 'right' })}</td>
                    <td style={{ ...celda, width: 100 }}>{inp(i, 'precioUnitario', { textAlign: 'right' })}</td>
                    <td style={{ ...celda, width: 60 }}>{inp(i, 'tasaIva', { textAlign: 'right' })}</td>
                    <td style={{ ...celda, width: 100, textAlign: 'right', fontFamily: 'var(--digit-font)', paddingTop: 10 }}>{fmt((Number(p.cantidad) || 0) * (Number(p.precioUnitario) || 0))}</td>
                    <td style={{ ...celda, width: 30 }}>
                      {!recibida && form.partidas.length > 1 && (
                        <button type="button" onClick={() => set('partidas', form.partidas.filter((_, j) => j !== i))} aria-label={`Quitar partida ${i + 1}`} title="Quitar partida"
                          style={{ ...css.btnGhost, padding: '4px 8px', fontSize: 12 }}>✕</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!recibida && (
            <button type="button" onClick={() => set('partidas', [...form.partidas, { codigo: '', descripcion: '', unidad: '', cantidad: '1', precioUnitario: '0', tasaIva: '16' }])}
              style={{ ...css.btnGhost, fontSize: 12, marginTop: 6 }}>+ Agregar partida</button>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 18, marginTop: 8, fontSize: 13, flexWrap: 'wrap' }}>
            <span>Subtotal <b style={{ fontFamily: 'var(--digit-font)' }}>{fmt(nuevo.subtotal)}</b></span>
            <span>IVA <b style={{ fontFamily: 'var(--digit-font)' }}>{fmt(nuevo.iva)}</b></span>
            <span>Total <b style={{ fontFamily: 'var(--digit-font)' }}>{fmt(nuevo.total)}</b>
              {Math.abs(nuevo.total - (orden.total || 0)) > 0.005 && <span style={{ color: 'var(--glass)' }}> (antes {fmt(orden.total)})</span>}
            </span>
          </div>
        </div>

        <div className="ui-grid" style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12, alignItems: 'start' }}>
          <div>
            <Label>Condición de pago</Label>
            <select value={form.condicionPago} onChange={e => set('condicionPago', e.target.value)} disabled={recibida} style={css.select(false)}>
              <option value="credito">Crédito</option>
              <option value="contado">Contado</option>
            </select>
          </div>
          <SelectorEntrega value={valorEntrega} onChange={setEntrega} direcciones={direcciones} sucursal={req.sucursal} />
        </div>
        <div><Label>Observaciones</Label><textarea rows={2} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} style={{ ...css.input(false), resize: 'vertical' }} /></div>
        <div>
          <Label required>Motivo del cambio</Label>
          <textarea rows={2} value={form.motivo} onChange={e => set('motivo', e.target.value)} placeholder="Ej. El proveedor actualizó el precio; se pidieron 10 y eran 12"
            style={{ ...css.input(!form.motivo.trim()), resize: 'vertical' }} />
          <div style={{ fontSize: 11.5, color: regresaAFinanzas ? '#b45309' : 'var(--glass)', marginTop: 4 }}>
            {regresaAFinanzas
              ? 'Finanzas ya había validado y el total sube: al guardar, la requisición regresa a su validación y se le avisa por correo.'
              : 'El cambio queda en la bitácora de la orden y en el historial de la requisición. La orden sube de revisión.'}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={enviando} style={css.btnGhost}>Cancelar</button>
          <button type="button" onClick={guardar} disabled={enviando} style={{ ...css.btn('#ff541c'), opacity: enviando ? 0.7 : 1 }}>{enviando ? 'Guardando…' : 'Guardar cambios'}</button>
        </div>
      </div>
    </Modal>
  );
}
